import { Badge } from "./badge";

/**
 * Optionen-Vergleich (Entscheidungen, Paket E-a; Gestaltungsvorschlag E2 A „Vergleichskarten am Desktop, aufklappbare Zeilen
 * darunter“, Betreiber vorläufig nach Empfehlung, am Live-Bild zu bestätigen; Design-Guide `optionen-vergleich.md`).
 * EIN Baustein, zwei Anordnungen aus denselben Daten:
 *  - ab `md` Karten nebeneinander (2 bis 4 Optionen), die empfohlene Option mit Ring `ring-primary-300` und Pille „Empfehlung“;
 *  - darunter aufklappbare Zeilen (native `<details>`, ohne Skript): Kopf mit Option, Titel und Pillen, Inhalt erst nach dem Aufklappen.
 * `anordnung` lässt eine Anordnung allein zu (Standard `auto` = beide, die Umschaltung macht die Fensterbreite).
 *
 * Der Baustein zeigt nur: er kennt weder Rechte noch Status. WELCHE Option „gewählt“ ist, liefert der Aufrufer (`gewaehlt`);
 * der Platz dafür (Pille „Gewählt“, Ring) ist hier vorgesehen, gespeichert wird die Wahl erst in Paket E-c. Gibt es eine
 * gewählte Option, trägt sie den Ring; die empfohlene behält dann nur ihre Pille (ein Ring je Vergleich, nie zwei).
 */
export interface VergleichsOption {
  id: string;
  /** Kennbuchstabe („A“, „B“). */
  key: string;
  titel: string;
  vorteile: readonly string[];
  nachteile: readonly string[];
  /** Folge / Kosten, Freitext. */
  folge?: string | null;
  empfohlen?: boolean;
  gewaehlt?: boolean;
}

export type VergleichsAnordnung = "auto" | "karten" | "zeilen";

// Feste, vollständige Klassenketten (nie zusammengesetzt, R8).
const KARTE = "flex flex-col gap-y-3 bg-card border border-card-line shadow-2xs rounded-xl p-4 sm:p-5";
const KARTE_HERVORGEHOBEN = "flex flex-col gap-y-3 bg-card border border-card-line shadow-2xs rounded-xl p-4 sm:p-5 ring-2 ring-primary-300";
const ZEILE = "group bg-card border border-card-line shadow-2xs rounded-xl";
const ZEILE_HERVORGEHOBEN = "group bg-card border border-card-line shadow-2xs rounded-xl ring-2 ring-primary-300";
// Raster nach Anzahl; „NUR_AB_MD“ ist dasselbe Raster, das unter `md` nicht erscheint (dort stehen die aufklappbaren Zeilen).
const RASTER: Record<number, string> = {
  1: "grid gap-4 grid-cols-1",
  2: "grid gap-4 grid-cols-1 md:grid-cols-2",
  3: "grid gap-4 grid-cols-1 md:grid-cols-3",
  4: "grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-4",
};
const RASTER_NUR_AB_MD: Record<number, string> = {
  1: "hidden md:grid gap-4 md:grid-cols-1",
  2: "hidden md:grid gap-4 md:grid-cols-2",
  3: "hidden md:grid gap-4 md:grid-cols-3",
  4: "hidden md:grid gap-4 md:grid-cols-2 xl:grid-cols-4",
};
const KOPFZEILE =
  "flex w-full cursor-pointer list-none items-center gap-x-3 rounded-xl p-4 text-start focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus";
const ZEILEN_LISTE = "space-y-3";
const ZEILEN_LISTE_NUR_BIS_MD = "md:hidden space-y-3";
const UEBERSCHRIFT_KLEIN = "text-xs font-semibold uppercase tracking-wide text-muted-foreground-1";

const SVG = {
  className: "shrink-0 size-4 mt-0.5",
  xmlns: "http://www.w3.org/2000/svg",
  width: 24,
  height: 24,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true as const,
};

function Pillen({ o }: { o: VergleichsOption }) {
  if (!o.empfohlen && !o.gewaehlt) return null;
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {o.gewaehlt && <Badge ton="erfolg">Gewählt</Badge>}
      {o.empfohlen && <Badge ton="primaer">Empfehlung</Badge>}
    </span>
  );
}

/** Inhalt einer Option, in beiden Anordnungen gleich. Leere Listen werden nicht gezeigt (keine Platzhalter). */
function Inhalt({ o }: { o: VergleichsOption }) {
  return (
    <div className="flex flex-col gap-y-3">
      {o.vorteile.length > 0 && (
        <div>
          <p className={UEBERSCHRIFT_KLEIN}>Vorteile</p>
          <ul className="mt-1.5 space-y-1.5">
            {o.vorteile.map((v, i) => (
              <li key={i} className="flex gap-x-2 text-sm text-foreground">
                {/* Symbol Plus (Lucide-Pfade inline, wie zeilen-symbole.tsx) */}
                <svg {...SVG}>
                  <path d="M5 12h14" />
                  <path d="M12 5v14" />
                </svg>
                <span className="min-w-0">{v}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {o.nachteile.length > 0 && (
        <div>
          <p className={UEBERSCHRIFT_KLEIN}>Nachteile</p>
          <ul className="mt-1.5 space-y-1.5">
            {o.nachteile.map((v, i) => (
              <li key={i} className="flex gap-x-2 text-sm text-foreground">
                {/* Symbol Minus (Lucide-Pfad inline) */}
                <svg {...SVG}>
                  <path d="M5 12h14" />
                </svg>
                <span className="min-w-0">{v}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {o.folge && (
        <div>
          <p className={UEBERSCHRIFT_KLEIN}>Folge / Kosten</p>
          <p className="mt-1.5 text-sm text-foreground whitespace-pre-line">{o.folge}</p>
        </div>
      )}
    </div>
  );
}

export function OptionenVergleich({
  id,
  optionen,
  beschriftung = "Optionen im Vergleich",
  anordnung = "auto",
}: {
  /** Eindeutige Id (verknüpft die Überschriften der Karten). */
  id: string;
  optionen: readonly VergleichsOption[];
  beschriftung?: string;
  anordnung?: VergleichsAnordnung;
}) {
  if (optionen.length === 0) return null;
  const hatGewaehlt = optionen.some((o) => o.gewaehlt);
  const hervor = (o: VergleichsOption) => (hatGewaehlt ? Boolean(o.gewaehlt) : Boolean(o.empfohlen));
  const anzahl = Math.min(Math.max(optionen.length, 1), 4);
  const zeigeKarten = anordnung !== "zeilen";
  const zeigeZeilen = anordnung !== "karten";
  // Beim Aufklappen gibt es eine geöffnete Zeile: die hervorgehobene, sonst die erste.
  const offenId = optionen.find(hervor)?.id ?? optionen[0]!.id;

  return (
    <>
      {/* Preline: marketing/marketing-sub-pricing-conversion/marketing-cat-pricing-cards/pricing-card-grid (nur Anordnung und die Hervorhebung einer Karte, kein Marketing-Inhalt) */}
      {/* Abweichung AW-048: Eigenbau aus Karten; mobile Anordnung mit nativem details statt der Skript-Variante des Accordions */}
      <div role="group" aria-label={beschriftung}>
        {zeigeKarten && (
          <div className={zeigeZeilen ? RASTER_NUR_AB_MD[anzahl] : RASTER[anzahl]}>
            {optionen.map((o) => (
              <article key={o.id} className={hervor(o) ? KARTE_HERVORGEHOBEN : KARTE} aria-labelledby={`${id}-${o.id}-karte`}>
                <div className="flex flex-col gap-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground-1">Option {o.key}</p>
                  <h3 id={`${id}-${o.id}-karte`} className="font-semibold text-foreground">
                    {o.titel}
                  </h3>
                  <Pillen o={o} />
                </div>
                <Inhalt o={o} />
              </article>
            ))}
          </div>
        )}
        {zeigeZeilen && (
          <div className={zeigeKarten ? ZEILEN_LISTE_NUR_BIS_MD : ZEILEN_LISTE}>
            {optionen.map((o) => (
              <details key={o.id} className={hervor(o) ? ZEILE_HERVORGEHOBEN : ZEILE} open={o.id === offenId}>
                {/* Preline: component/accordion/accordion-always-open (Anordnung; Kopf mit Pillen, Zustand im Browser) */}
                <summary className={KOPFZEILE}>
                  <span className="flex min-w-0 grow flex-col gap-y-1">
                    <span className="text-xs font-medium text-muted-foreground-1">Option {o.key}</span>
                    <span className="font-semibold text-foreground">{o.titel}</span>
                    <Pillen o={o} />
                  </span>
                  {/* Symbol ChevronDown (Lucide-Pfad inline), dreht beim Öffnen */}
                  <svg {...SVG} className="shrink-0 size-4 text-muted-foreground-1 group-open:rotate-180">
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </summary>
                <div className="px-4 pb-4">
                  <Inhalt o={o} />
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
